import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDownRight } from "lucide-react";
import { WHATSAPP_LINK, scrollToSection } from "../lib/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1641122098360-13e4442bb539?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxtdW1iYWklMjBhcmNoaXRlY3R1cmUlMjBtb2Rlcm4lMjBkYXJrfGVufDB8fHx8MTc4NTkxNzEzOXww&ixlib=rb-4.1.0&q=85";

const lines = [
  { text: "Launch your brand", accent: false },
  { text: "on every marketplace", accent: false },
  { text: "India shops on.", accent: true },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={HERO_IMG}
          alt="Mumbai architecture at night"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-midnight/80" />
      <div className="absolute inset-0 bg-gradient-to-t from-midnight via-transparent to-midnight/60" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 w-full pt-32 pb-20"
      >
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-4 mb-10"
        >
          <span className="h-px w-12 bg-gold" />
          <span
            data-testid="hero-eyebrow"
            className="text-xs font-bold uppercase tracking-[0.25em] text-gold"
          >
            Ecommerce Consultancy — Mumbai
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display font-900 tracking-tighter leading-[0.92] text-[13vw] sm:text-[10vw] lg:text-[7.5rem] max-w-6xl"
        >
          {lines.map((line, i) => (
            <MaskedLine key={line.text} delay={0.55 + i * 0.14}>
              {line.accent ? (
                <span className="font-accent italic font-medium text-terracotta">
                  {line.text}
                </span>
              ) : (
                line.text
              )}
            </MaskedLine>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subcopy"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-coal/70"
        >
          Amazon, Shopify, Blinkit, Zepto, Instamart — we take your brand from
          seller account to scale. Setup, listings, content and growth, handled
          end-to-end by operators who have done it before.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <motion.a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-cta"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-3 bg-terracotta text-night font-bold text-sm uppercase tracking-[0.15em] px-8 py-5 hover:bg-gold transition-colors duration-300"
          >
            <MessageCircle size={18} strokeWidth={2.5} />
            Chat on WhatsApp
          </motion.a>
          <motion.button
            data-testid="hero-contact-cta"
            onClick={() => scrollToSection("#contact")}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-3 border border-white/25 text-coal font-bold text-sm uppercase tracking-[0.15em] px-8 py-5 hover:border-terracotta hover:text-terracotta transition-colors duration-300"
          >
            Get a Free Audit
            <ArrowDownRight size={18} strokeWidth={2.5} />
          </motion.button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          data-testid="hero-meta-strip"
          className="mt-20 flex flex-wrap gap-x-10 gap-y-3 text-xs font-bold uppercase tracking-[0.2em] text-coal/40"
        >
          <span>40+ brands launched</span>
          <span className="text-terracotta">·</span>
          <span>5 marketplaces covered</span>
          <span className="text-terracotta">·</span>
          <span>Mumbai → PAN India</span>
        </motion.div>
      </motion.div>
    </section>
  );
};
